import React from 'react';
import { Container } from '../ui';
import heroVideoSecondary from '../../assets/istatec_cover_secondary.webm';

interface PageHeroProps {
  /** Optional badge element (typically a <Badge>...</Badge> with mb-6). */
  badge?: React.ReactNode;
  /** Main heading text or JSX. */
  title: React.ReactNode;
  /** Optional subheading paragraph. */
  subtitle?: React.ReactNode;
  /** Additional content below the subtitle (e.g. CTA buttons). */
  children?: React.ReactNode;
  /** Extra classes appended to the inner content wrapper (e.g. `-mt-10` to nudge up). */
  contentClassName?: string;
}

/**
 * Shared page hero for inner pages. Renders the secondary cover video
 * as background with badge / title / subtitle overlaid on top, matching
 * the visual language established by the home page's secondary cover.
 */
export const PageHero: React.FC<PageHeroProps> = ({
  badge,
  title,
  subtitle,
  children,
  contentClassName,
}) => {
  return (
    <section className="relative overflow-hidden bg-navy-950 pt-20">
      {/* Background Video — fills the entire section on every viewport.
          `object-cover` guarantees the aspect ratio never breaks on mobile
          (the previous `w-full h-auto` shrank the video down and left dead
          navy space on narrow screens).
          NOTE: intentionally NO darkening overlay — the video's own
          brightness is the effect we want on inner-page heroes. */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src={heroVideoSecondary}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />

      <Container className="relative z-10 py-16 md:py-24">
        <div
          className={
            'max-w-3xl mx-auto text-center' +
            (contentClassName ? ` ${contentClassName}` : '')
          }
        >
          {badge}
          <h1 className="heading-xl text-white mb-6 drop-shadow">{title}</h1>
          {subtitle && (
            <p className="text-lg sm:text-xl text-white leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              {subtitle}
            </p>
          )}
          {children && <div className="mt-8">{children}</div>}
        </div>
      </Container>
    </section>
  );
};
